import { ChangeDetectionStrategy, Component, inject, ChangeDetectorRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { FIELD_CONTROL, FIELD_CONFIG } from './field-tokens';
import { ImageCropperDialogComponent } from '../image-cropper/image-cropper-dialog.component';
import { UiOrchestratorService } from '../../../core/services/ui-orchestrator.service';

const MAX_FILE_BYTES = 5 * 1024 * 1024;

@Component({
  selector: 'app-field-photo-upload',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, ImageCropperDialogComponent],
  template: `
    <div class="flex items-center gap-4">
      <div class="relative w-20 h-20 rounded-full overflow-hidden border-2
                  border-surface-200 dark:border-surface-700
                  bg-surface-100 dark:bg-surface-800 shrink-0">
        @if (formControl.value) {
          <img [src]="formControl.value" alt="Profile" class="w-full h-full object-cover" />
        } @else {
          <div class="flex items-center justify-center w-full h-full text-surface-400">
            <i class="pi pi-user text-3xl"></i>
          </div>
        }
      </div>
      <div class="flex flex-col gap-2">
        <label class="p-button p-button-outlined p-button-secondary p-button-sm
                      cursor-pointer inline-flex items-center gap-2 w-fit"
               [class.p-disabled]="formControl.disabled">
          <i class="pi pi-upload text-xs"></i>
          <span class="text-sm">{{ config.placeholder || (formControl.value ? 'Change Photo' : 'Upload Photo') }}</span>
          <input type="file" accept="image/*" class="hidden"
                 [disabled]="formControl.disabled" (change)="onFileChange($event)" />
        </label>
        <div class="flex items-center gap-3">
          @if (formControl.value && !formControl.disabled) {
            @if (canRecrop()) {
              <button type="button"
                class="text-xs text-primary-500 hover:text-primary-600"
                (click)="recrop()">
                Adjust crop
              </button>
            }
            <button type="button"
              class="text-xs text-red-500 hover:text-red-600"
              (click)="removePhoto()">
              Remove photo
            </button>
          }
        </div>
      </div>
    </div>

    <app-image-cropper-dialog
      [(visible)]="cropperVisible"
      [imageSrc]="pendingImage()"
      [header]="'Crop ' + config.label"
      shape="circle"
      (cropped)="onCropped($event)"
      (cancelled)="pendingImage.set(null)" />
  `
})
export class PhotoUploadFieldComponent {
  readonly config  = inject(FIELD_CONFIG);
  readonly control = inject(FIELD_CONTROL);
  readonly cdr     = inject(ChangeDetectorRef);
  private readonly ui = inject(UiOrchestratorService);

  get formControl(): FormControl { return this.control as FormControl; }

  protected cropperVisible = signal(false);
  /** Image being cropped — the untouched upload, so re-cropping never compounds quality loss. */
  protected pendingImage = signal<string | null>(null);

  private originalImage: string | null = null;

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.ui.toast({ severity: 'error', summary: 'Invalid file', detail: 'Please select an image file.' });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.ui.toast({ severity: 'error', summary: 'File too large', detail: 'Images must be 5 MB or smaller.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.originalImage = reader.result as string;
      this.pendingImage.set(this.originalImage);
      this.cropperVisible.set(true);
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      this.ui.toast({ severity: 'error', summary: 'Upload failed', detail: 'Could not read the selected image.' });
    };
    reader.readAsDataURL(file);
  }

  /**
   * A stored value that is a remote URL can't be re-cropped: drawing it to a canvas
   * taints it, so nothing can be exported. Those users re-upload instead.
   */
  canRecrop(): boolean {
    return !!this.originalImage || String(this.formControl.value ?? '').startsWith('data:');
  }

  /** Re-open the cropper. Falls back to the stored value when the original is gone (edit mode). */
  recrop(): void {
    const source = this.originalImage ?? this.formControl.value;
    if (!source) return;
    this.pendingImage.set(source);
    this.cropperVisible.set(true);
  }

  onCropped(dataUrl: string): void {
    this.formControl.setValue(dataUrl);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.pendingImage.set(null);
    this.cdr.markForCheck();
  }

  removePhoto(): void {
    this.originalImage = null;
    this.formControl.setValue(null);
    this.formControl.markAsDirty();
    this.cdr.markForCheck();
  }
}
